import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, TextField,
  TableCell, TableRow, Chip, IconButton,
  Avatar, Checkbox, FormControl, InputLabel, Select, MenuItem, ButtonBase,
  InputAdornment
} from '@mui/material';
import {
  Add, Delete, Search, KeyboardArrowDown, Group
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { api, ENDPOINTS } from '../../services';
import { fetchSpaces, createSpace, updateSpace, deleteSpace } from '../../store/spaceSlice';
import { COLORS } from '../../shared/colors';
import { SIZING } from '../../shared/globalSX';
import { CHARACTER_LIMITS, MANAGEMENT_FILTER_STATUS } from '../../shared/appConstants';
import { ICONS } from '../../shared/icons';
import Navbar from '../shared/Navbar';
import '../users/UserManagement.css';
import OverflowTooltip from '../../shared/OverflowTooltip';
import CommonTable from '../shared/CommonTable';
import CommonDeleteConfirmDialog from '../shared/CommonDeleteConfirmDialog';
import CommonFormModal from '../shared/CommonFormModal';
import TeamMembersDialog from '../shared/TeamMembersDialog';

const SPACE_STAT_CARDS = [
  {
    key: 'total_spaces',
    labelKey: 'spaces.totalSpaces',
    colorKey: 'spaces',
    iconSrc: ICONS.totalSpace
  },
  {
    key: 'active_projects',
    labelKey: 'spaces.activeProjects',
    colorKey: 'projects',
    iconSrc: ICONS.activeProjects
  },
  {
    key: 'total_users',
    labelKey: 'spaces.totalUsers',
    colorKey: 'users',
    iconSrc: ICONS.totalUsers
  },
  {
    key: 'inactive_projects',
    labelKey: 'spaces.inactiveProjects',
    colorKey: 'inactiveProjects',
    iconSrc: ICONS.inactiveProject
  }
];

const ACTIVE_STATUS_CHIP_SX = {
  bgcolor: '#E9FED8',
  color: '#538D22',
  border: '1px solid #538D22',
  fontWeight: 600
};

const SOFT_DELETE_STATUS_CHIP_SX = {
  bgcolor: '#FDECEC',
  color: '#D92D20',
  border: '1px solid #D92D20',
  fontWeight: 600
};

const getSpaceStatusChipProps = (status) => {
  const normalizedStatus = (status || '').trim().toUpperCase();

  if (normalizedStatus === 'SOFT-DELETE') {
    return {
      label: 'SOFT-DELETE',
      sx: SOFT_DELETE_STATUS_CHIP_SX
    };
  }

  if (normalizedStatus) {
    return {
      label: normalizedStatus,
      sx: ACTIVE_STATUS_CHIP_SX
    };
  }

  return {
    label: 'ACTIVE',
    sx: ACTIVE_STATUS_CHIP_SX
  };
};


const createSpaceModalStyles = {
  title: {
    pb: { xs: '0px !important', lg: 1 },
    position: 'relative'
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    color: 'text.primary'
  },
  heading: {
    fontSize: '1.125rem',
    lineHeight: 1.2,
    fontWeight: 700,
    color: COLORS.pureBlack,
    mb: '0.75rem'
  },
  subtitle: {
    fontSize: '0.875rem',
    lineHeight: 1.25,
    color: COLORS.pureBlack,
    fontWeight: 400,
    mb: { xs: 0, lg: '1rem' }
  },
  content: {
    px: '1.5rem',
    pt: { xs: '0px !important', lg: '1rem' },
    pb: '0.75rem'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: '1rem',
    color: COLORS.pureBlack
  },
  sectionTitle: {
    fontSize: '1.1rem',
    lineHeight: 1.2,
    fontWeight: 400,
    color: COLORS.pureBlack
  },
  fieldWrap: {
    mb: '1rem'
  },
  field: {
    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: COLORS.brand.accent },
    '& .MuiInputLabel-root.Mui-focused': { color: COLORS.brand.accent }
  },
  counter: {
    display: 'flex',
    justifyContent: 'flex-end',
    mt: 1,
    pr: 0.5,
    color: COLORS.pureBlack,
    fontSize: '0.78rem'
  },
  selectGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 1,
    mt: '1.25rem',
    '@media (max-width: 700px)': {
      gridTemplateColumns: '1fr',
      mt: '0.75rem'
    }
  },
  select: {
    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: COLORS.brand.accent },
    '& .MuiInputLabel-root.Mui-focused': { color: COLORS.brand.accent }
  },
  selectValue: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  menuUserRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    minWidth: 0,
    flex: 1
  },
  menuAvatar: {
    bgcolor: '#082B72',
    width: 28,
    height: 28,
    fontSize: '12px'
  },
  menuText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  actions: {
    px: '32px',
    pt: 1.5,
    pb: 3,
    justifyContent: 'space-between',
    gap: 2,
    '& > :not(style) ~ :not(style)': {
      ml: 0
    }
  },
  cancelButton: {
    minWidth: 150,
    px: 4,
    py: 1,
    borderRadius: '6px',
    borderColor: COLORS.ui.border,
    color: COLORS.pureBlack,
    fontSize: '0.9rem',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      borderColor: COLORS.gray[400],
      bgcolor: COLORS.gray[50]
    }
  },
  createButton: {
    minWidth: 160,
    px: 4,
    py: 1,
    borderRadius: '6px',
    bgcolor: COLORS.brand.primaryDark,
    color: COLORS.white,
    fontSize: '0.9rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      bgcolor: COLORS.brand.primaryDarkHover,
      boxShadow: 'none'
    },
    '&.Mui-disabled': {
      bgcolor: COLORS.gray[300],
      color: COLORS.white
    }
  }
};

const createSpaceMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 300,
      width: 350,
      borderRadius: '12px',
      marginTop: '8px',
      boxShadow: '4px 4px 4px 0px #9EB3DB5E',
      border: '0.6px solid transparent',
      backgroundImage: 'linear-gradient(white, white), linear-gradient(179.65deg, #B7CBF2 0.33%, #5374B7 99.73%)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box'
    }
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' }
};

const dropdownMenuItemSx = {
  py: 1.5,
  px: 2,
  mx: 1,
  borderRadius: '6px',
  fontSize: '0.875rem',
  gap: 1.5,
  borderBottom: '1.5px solid #E5E7EB',
  '&:hover': { bgcolor: COLORS.ui.menuHover },
  '&:last-of-type': {
    borderBottom: 'none'
  }
};

const getSpaceMembers = (space) => {
  if (Array.isArray(space?.users)) return space.users;
  if (Array.isArray(space?.members)) return space.members;
  return [];
};

const spaceManagementStyles = {
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 1,
    flexWrap: 'nowrap',
    minWidth: 104
  },
  actionIcon: {
    color: COLORS.brand.primaryDark
  },
  actionImage: {
    width: 18,
    height: 18
  }
};

const SPACE_TABLE_ACTIONS_WIDTH = '128px';

const SpaceManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [users, setUsers] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [formData, setFormData] = useState({ name: '', description: '', lead_id: '' });
  const [editFormData, setEditFormData] = useState({ name: '', description: '', lead_id: '' });
  const [originalEditFormData, setOriginalEditFormData] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [editSelectedMembers, setEditSelectedMembers] = useState([]);
  const [originalEditSelectedMembers, setOriginalEditSelectedMembers] = useState([]);
  const [openedSelectedMembers, setOpenedSelectedMembers] = useState([]);
  const [openedEditSelectedMembers, setOpenedEditSelectedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [spaceSearchTerm, setSpaceSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isCreatingSpace, setIsCreatingSpace] = useState(false);
  const [isUpdatingSpace, setIsUpdatingSpace] = useState(false);
  const [isDeletingSpace, setIsDeletingSpace] = useState(false);

  const initialLoadRef = useRef(false);
  const prevSearchTermRef = useRef('');
  const [widgets, setWidgets] = useState({ total_spaces: 0, total_users: 0, total_projects: 0, inactive_projects: 0 });
  const [widgetsLoading, setWidgetsLoading] = useState(true);
  const { user } = useSelector((state) => state.auth);
  const { loading: reduxLoading } = useSelector((state) => state.spaces);
  const dispatch = useDispatch();
  const limit =8;

  const normalizeMemberIds = (memberIds = []) => (
    Array.from(new Set(memberIds.filter(Boolean))).sort()
  );

  const isEditSpaceUnchanged = React.useMemo(() => {
    if (!originalEditFormData) return true;
    const normalizeSpaceForm = (data) => ({
      name: data.name || '',
      description: data.description || '',
      lead_id: data.lead_id || ''
    });

    return (
      JSON.stringify(normalizeSpaceForm(editFormData)) === JSON.stringify(normalizeSpaceForm(originalEditFormData)) &&
      JSON.stringify(normalizeMemberIds(editSelectedMembers)) === JSON.stringify(normalizeMemberIds(originalEditSelectedMembers))
    );
  }, [editFormData, editSelectedMembers, originalEditFormData, originalEditSelectedMembers]);

  const fetchUsers = async () => {
    try {
      const response = await api.get(ENDPOINTS.USERS_ALL);
      if (response?.data?.data && Array.isArray(response.data.data)) {
        setUsers(response.data.data);
      } else {
        setUsers([]);
        console.warn('Invalid users data received:', response?.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchWidgets = async () => {
    try {
      setWidgetsLoading(true);
      const response = await api.get(ENDPOINTS.SPACES_WIDGETS);
      if (response?.data?.data && typeof response.data.data === 'object') {
        const widgetData = response.data.data;
        setWidgets({
          total_spaces: typeof widgetData.total_spaces === 'number' ? widgetData.total_spaces : 0,
          total_users: typeof widgetData.total_users === 'number' ? widgetData.total_users : 0,
          total_projects: typeof widgetData.total_projects === 'number' ? widgetData.total_projects : 0,
          inactive_projects: typeof widgetData.inactive_projects === 'number' ? widgetData.inactive_projects : 0
        });
      } else {
        console.warn('Invalid widgets data received:', response?.data);
      }
    } catch (error) {
      console.error('Error fetching widgets:', error);
    } finally {
      setWidgetsLoading(false);
    }
  };

  const fetchSpacesData = useCallback(async (page = currentPage, searchName = spaceSearchTerm) => {
    const result = await dispatch(fetchSpaces({ page, limit, name: searchName }));
    if (result.payload && typeof result.payload === 'object') {
      const newSpaces = Array.isArray(result.payload.spaces) ? result.payload.spaces : [];
      const totalCount = result.payload.total || 0;
      
      if (page === 1) {
        setSpaces(newSpaces);
      } else {
        setSpaces(prev => {
          const existingIds = new Set(prev?.map(space => space.id));
          const uniqueNewSpaces = newSpaces.filter(space => !existingIds.has(space.id));
          return [...prev, ...uniqueNewSpaces];
        });
      }
      
      setHasMore(page * limit < totalCount);
      setCurrentPage(page);
    } else {
      console.warn('Invalid spaces data received:', result.payload);
      if (page === 1) {
        setSpaces([]);
      }
    }
  }, [dispatch, currentPage, spaceSearchTerm, limit]);

  useEffect(() => {
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;
    
    fetchUsers();
    fetchSpacesData();
    fetchWidgets();
  }, [fetchSpacesData]);

  useEffect(() => {
    if (spaceSearchTerm === prevSearchTermRef.current) return;
    
    const debounceTimer = setTimeout(() => {
      setCurrentPage(1);
      setHasMore(true);
      fetchSpacesData(1, spaceSearchTerm);
      prevSearchTermRef.current = spaceSearchTerm;
    }, 700);

    return () => clearTimeout(debounceTimer);
  }, [spaceSearchTerm, fetchSpacesData]);

  const scrollContainerRef = useRef(null);
  const autoLoadRef = useRef(false);

  // Scroll listener effect
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop <= clientHeight + 10 && hasMore && !reduxLoading && !autoLoadRef.current) {
        autoLoadRef.current = true;
        fetchSpacesData(currentPage + 1).finally(() => {
          autoLoadRef.current = false;
        });
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, reduxLoading, currentPage, fetchSpacesData]);

  // Auto-load effect
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasMore || reduxLoading || autoLoadRef.current || spaces.length === 0) return;
    
    if (container.scrollHeight <= container.clientHeight) {
      autoLoadRef.current = true;
      fetchSpacesData(currentPage + 1).finally(() => {
        autoLoadRef.current = false;
      });
    }
  }, [spaces.length, hasMore, reduxLoading, currentPage, fetchSpacesData]);

  const openCreateSpace = () => {
    setFormData({ name: '', description: '', lead_id: '' });
    setSelectedMembers([]);
    setOpenedSelectedMembers([]);
    setShowCreateModal(true);
  };
  
  const closeCreateSpace = (options = {}) => {
    const force = options?.force === true;
    if (isCreatingSpace && !force) return;
    setShowCreateModal(false);
    setFormData({ name: '', description: '', lead_id: '' });
    setSelectedMembers([]);
    setOpenedSelectedMembers([]);
    setSearchTerm('');
  };

  const openEditSpace = (spaceId) => {
    const space = spaces?.find(s => s?.id === spaceId);
    if (space) {
      const spaceLeadUser = users?.find(u => u?.name === space?.owner_name);
      const nextEditFormData = {
        name: space?.name || '',
        description: space?.description || '',
        lead_id: spaceLeadUser?.id || ''
      };
      const activeUserIds = new Set(users?.map(u => u?.id).filter(Boolean));
      const nextEditSelectedMembers = (space?.users?.map(u => u?.id) || []).filter(id => activeUserIds.has(id));
      setEditFormData(nextEditFormData);
      setOriginalEditFormData(nextEditFormData);
      setEditSelectedMembers(nextEditSelectedMembers);
      setOriginalEditSelectedMembers(nextEditSelectedMembers);
      setOpenedEditSelectedMembers([]);
    }
    setSelectedSpace(space);
    setShowEditModal(true);
  };
  const closeEditSpace = (options = {}) => {
    const force = options?.force === true;
    if (isUpdatingSpace && !force) return;
    setShowEditModal(false);
    setSelectedSpace(null);
    setEditFormData({ name: '', description: '', lead_id: '' });
    setOriginalEditFormData(null);
    setEditSelectedMembers([]);
    setOriginalEditSelectedMembers([]);
    setOpenedEditSelectedMembers([]);
    setSearchTerm('');
  };

  const openDeleteSpace = (spaceId, spaceName) => {
    setSelectedSpace({ id: spaceId, name: spaceName });
    setShowDeleteModal(true);
  };
  const closeDeleteSpace = () => {
    if (isDeletingSpace) return;
    setShowDeleteModal(false);
    setSelectedSpace(null);
  };

  const viewMembers = (space) => {
    if (!space?.id) return;

    setSelectedSpace(space);
    setShowMembersModal(true);
  };

  const closeMembersModal = () => {
    setShowMembersModal(false);
    setSelectedSpace(null);
  };

  const confirmDeleteSpace = async () => {
    if (isDeletingSpace || !selectedSpace?.id) return;

    setIsDeletingSpace(true);
    try {
      await dispatch(deleteSpace(selectedSpace.id)).unwrap();
      toast.success(t('spaces.spaceDeletedSuccess'));
      closeDeleteSpace();
      fetchSpacesData(1, spaceSearchTerm);
      fetchWidgets();
    } catch (error) {
      console.error('Error deleting space:', error);
      toast.error(t('spaces.deleteSpaceError'));
    } finally {
      setIsDeletingSpace(false);
    }
  };

  const createSpaceHandler = async () => {
    if (isCreatingSpace) return;

    if (!formData.name.trim()) {
      toast.error(t('spaces.enterSpaceNameError'));
      return;
    }
    
    if (!formData.lead_id) {
      toast.error(t('spaces.selectSpaceLeadError'));
      return;
    }
    
    const spaceData = {
      name: formData.name,
      workspace_id: user?.workspace_id,
      lead_id: formData.lead_id,
      description: formData.description,
      members: selectedMembers
    };

    setIsCreatingSpace(true);
    try {
      await dispatch(createSpace(spaceData)).unwrap();
      toast.success(t('spaces.spaceCreatedSuccess'));
      closeCreateSpace({ force: true });
      fetchSpacesData(1, spaceSearchTerm);
      fetchWidgets();
    } catch (error) {
      console.error('Error creating space:', error);
      toast.error(t('spaces.createSpaceError'));
    } finally {
      setIsCreatingSpace(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'lead_id' && value) {
      setSelectedMembers(prev => (prev.includes(value) ? prev : [...prev, value]));
    }
  };

  const handleMembersSelectChange = (event) => {
    const value = event.target.value;
    const nextMembers = typeof value === 'string' ? value.split(',') : value;
    if (formData.lead_id && !nextMembers.includes(formData.lead_id)) {
      setFormData(prevForm => ({ ...prevForm, lead_id: '' }));
    }
    setSelectedMembers(nextMembers);
  };

  const getUserInitials = (name = '') => {
    const nameParts = name.split(' ').filter(Boolean);
    if (nameParts.length >= 2) {
      return `${nameParts[0]?.[0] || ''}${nameParts[nameParts.length - 1]?.[0] || ''}`.toUpperCase();
    }
    return (nameParts[0]?.[0] || '').toUpperCase();
  };

  const getUserNameById = (userId) => users?.find(user => user?.id === userId)?.name || '';

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'lead_id' && value) {
      setEditSelectedMembers(prev => (prev.includes(value) ? prev : [...prev, value]));
    }
  };

  const updateSpaceHandler = async () => {
    if (isUpdatingSpace || isEditSpaceUnchanged) return;

    if (!editFormData.name.trim()) {
      toast.error(t('spaces.enterSpaceNameError'));
      return;
    }
    
    if (!editFormData.lead_id) {
      toast.error(t('spaces.selectSpaceLeadError'));
      return;
    }
    
    const spaceData = {
      name: editFormData.name,
      workspace_id: user?.workspace_id,
      lead_id: editFormData.lead_id,
      description: editFormData.description,
      members: editSelectedMembers
    };

    setIsUpdatingSpace(true);
    try {
      await dispatch(updateSpace({ id: selectedSpace.id, data: spaceData })).unwrap();
      toast.success(t('spaces.spaceUpdatedSuccess'));
      closeEditSpace({ force: true });
      fetchSpacesData(1, spaceSearchTerm);
    } catch (error) {
      console.error('Error updating space:', error);
      toast.error(t('spaces.updateSpaceError'));
    } finally {
      setIsUpdatingSpace(false);
    }
  };

  const renderStatCard = ({ key, labelKey, colorKey, iconSrc }) => {
    const palette = COLORS.spaceDashboard.cards[colorKey];
    const totalProjects = Number(widgets.total_projects) || 0;
    const inactiveProjects = Number(widgets.inactive_projects) || 0;
    const activeProjects = Math.max(totalProjects - inactiveProjects, 0);
    const statValue = key === 'active_projects' ? activeProjects : widgets[key];
    const value = widgetsLoading ? t('common.loading') : statValue;

    const handleCardClick = () => {
      if (widgetsLoading) return;

      if (key === 'total_spaces') {
        setSpaceSearchTerm('');
        prevSearchTermRef.current = '';
        setCurrentPage(1);
        setHasMore(true);
        if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
        navigate('/space-management', { replace: true });
        fetchSpacesData(1, '');
        return;
      }

      if (key === 'total_users') {
        navigate(`/user-management?status=${MANAGEMENT_FILTER_STATUS.ALL}`);
        return;
      }

      const status = key === 'active_projects' ? MANAGEMENT_FILTER_STATUS.ACTIVE : MANAGEMENT_FILTER_STATUS.INACTIVE;
      navigate(`/project-management?status=${status}`);
    };

    return (
      <Card
        key={key}
        className="space-stat-card"
        elevation={0}
        role="button"
        tabIndex={widgetsLoading ? -1 : 0}
        aria-label={t(labelKey)}
        aria-disabled={widgetsLoading}
        onClick={handleCardClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleCardClick();
          }
        }}
        sx={{
          flex: '1 1 0',
          minWidth: 180,
          borderRadius: '8px',
          border: `1px solid ${palette.border} !important`,
          bgcolor: palette.background,
          boxShadow: 'none',
          cursor: widgetsLoading ? 'default' : 'pointer',
          transition: 'transform 160ms ease, box-shadow 160ms ease',
          '&:hover': widgetsLoading ? {} : {
            transform: 'translateY(-2px)',
            boxShadow: '0 5px 14px rgba(8, 43, 114, 0.14)'
          },
          '&:focus-visible': {
            outline: `3px solid ${COLORS.brand.primaryDark}`,
            outlineOffset: '2px'
          }
        }}
      >
        <CardContent sx={{ px: 2.5, py: 2, '&:last-child': { pb: 2 } }}>
          <Box display="flex" alignItems="center" gap={2.25}>
            <Box
              component="img"
              src={iconSrc}
              alt=""
              aria-hidden="true"
              sx={{
                width: 28,
                height: 28,
                flex: '0 0 auto'
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: palette.text,
                flex: 1,
                fontSize: '1.1rem',
                fontWeight: 700,
                lineHeight: 1.2,
                minWidth: 0
              }}
            >
              {t(labelKey)}
            </Typography>
            {widgetsLoading ? (
              <Typography
                variant="body2"
                sx={{
                  color: palette.text,
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  lineHeight: 1,
                  textAlign: 'right',
                  whiteSpace: 'nowrap'
                }}
              >
                {value}
              </Typography>
            ) : (
              <Typography
                variant="h4"
                sx={{
                  color: palette.text,
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  textAlign: 'right',
                  textShadow: '0 2px 2px rgba(0, 0, 0, 0.22)',
                  whiteSpace: 'nowrap'
                }}
              >
                {value}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const tableColumns = [
    { id: 'spaceName', label: t('spaces.spaceName'), width: `calc((100% - ${SPACE_TABLE_ACTIONS_WIDTH}) * 0.2556)`, maxWidth: '200px' },
    { id: 'spaceLead', label: t('spaces.spaceLead'), width: `calc((100% - ${SPACE_TABLE_ACTIONS_WIDTH}) * 0.2)`, maxWidth: '150px' },
    { id: 'members', label: t('spaces.members'), width: `calc((100% - ${SPACE_TABLE_ACTIONS_WIDTH}) * 0.1556)` },
    { id: 'projects', label: t('spaces.projects'), width: `calc((100% - ${SPACE_TABLE_ACTIONS_WIDTH}) * 0.1333)` },
    { id: 'created', label: t('spaces.created'), width: `calc((100% - ${SPACE_TABLE_ACTIONS_WIDTH}) * 0.1444)` },
    { id: 'status', label: t('common.status'), width: `calc((100% - ${SPACE_TABLE_ACTIONS_WIDTH}) * 0.1111)` },
    { id: 'actions', label: t('common.actions'), width: SPACE_TABLE_ACTIONS_WIDTH, align: 'right' }
  ];

  const renderTableRow = (space) => (
    <TableRow key={space.id}>
      <TableCell sx={{ maxWidth: '200px', overflow: 'hidden' }}>
        <Box sx={{ width: '100%' }}>
          <OverflowTooltip
            title={space?.name || ''}
            variant="body2"
            fontWeight="500"
            tooltipWidth={400}
            placement="top-start"
          />
          {space?.description && (
            <OverflowTooltip
              title={space.description}
              variant="caption"
              color="text.secondary"
              tooltipWidth={600}
              placement="top-start"
              sx={{ display: 'block', whiteSpace: 'nowrap' }}
            />
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ maxWidth: '150px', overflow: 'hidden' }}>
        <OverflowTooltip
          title={space?.owner_name || t('common.notAvailable')}
          variant="body2"
          tooltipWidth={400}
          placement="top-start"
        />
      </TableCell>
      <TableCell>{space?.user_count || 0} {t('spaces.members')}</TableCell>
      <TableCell>
        {Number(space?.project_count) > 0 ? (
          <ButtonBase
            onClick={() => {
              if (space?.id === undefined || space?.id === null || space.id === '') {
                console.error('Invalid space data for project navigation');
                return;
              }
              navigate(`/project-management?status=${MANAGEMENT_FILTER_STATUS.ALL}&spaceId=${encodeURIComponent(space.id)}`);
            }}
            aria-label={`${space.project_count} ${t('spaces.projects')} - ${space?.name || ''}`}
            sx={{
              color: COLORS.brand.primaryDark,
              fontWeight: 600,
              borderRadius: '3px',
              px: 0.5,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
              '&:focus-visible': {
                outline: `2px solid ${COLORS.brand.primaryDark}`,
                outlineOffset: '2px'
              }
            }}
          >
            {space.project_count}
          </ButtonBase>
        ) : 0}
      </TableCell>
      <TableCell>{space?.created_at ? new Date(space.created_at).toLocaleDateString() : t('common.notAvailable')}</TableCell>
      <TableCell>
        {(() => {
          const statusChip = getSpaceStatusChipProps(space?.status);

          return (
            <Chip
              label={statusChip.label}
              size="small"
              sx={statusChip.sx}
            />
          );
        })()}
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Box sx={spaceManagementStyles.actionButtons}>
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              viewMembers(space);
            }}
            title={t('spaces.teamMembers')}
            aria-label={t('spaces.teamMembers')}
          >
            <Group fontSize="small" sx={spaceManagementStyles.actionIcon} />
          </IconButton>
          <IconButton size="small" onClick={() => openEditSpace(space?.id)}>
            <Box component="img" src={ICONS.editBlue} alt="Edit" sx={spaceManagementStyles.actionImage} />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => openDeleteSpace(space?.id, space?.name)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );



  return (
    <div className="dashboard">

      <Navbar />

      {/* Main Content */}
      <main className="main" style={{ display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1, borderRadius: '8px' }}>
          <Typography variant="h6" fontWeight={600} color={COLORS.text.muted}>
            {t('spaces.spaceManagement')}
          </Typography>
        </Box>

        <Box className="user-management-header" sx={{ mb: SIZING.margin.md, bgcolor: COLORS.tableHeaderBg, borderBottom: `1px solid ${COLORS.brand.primaryDark}`, py: '0.25rem' }}>
          <Typography variant="h6" fontWeight="700" sx={{ color: COLORS.brand.primaryDark, fontSize: SIZING.font.md, m: 0 }}>
            {t('spaces.allSpaces')}
          </Typography>
          <Box display="flex" gap={SIZING.gap.sm}>
            <TextField
              size="small"
              placeholder={t('spaces.searchSpaces')}
              value={spaceSearchTerm}
              onChange={(e) => setSpaceSearchTerm(e.target.value)}
              className="user-search-input pm-action-search"
              sx={{ bgcolor: COLORS.white, '& .MuiOutlinedInput-root': { bgcolor: COLORS.white } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 20, color: COLORS.brand.primaryDark }} />
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreateSpace}
              className="pm-action-button"
              sx={{ bgcolor: COLORS.ui.buttonPaleBlue, color: COLORS.brand.primaryDark, boxShadow: 'none', '&:hover': { bgcolor: COLORS.ui.buttonPaleBlueHover, boxShadow: 'none' }, textTransform: 'none', fontWeight: 600, borderRadius: '4px' }}
            >
              {t('spaces.createSpace')}
            </Button>
          </Box>
        </Box>

        <Box display="flex" gap={3} flexWrap="wrap" sx={{ mb: 2 }}>
          {SPACE_STAT_CARDS.map(renderStatCard)}
        </Box>

        <CommonTable
          columns={tableColumns}
          data={spaces}
          renderRow={renderTableRow}
          loading={reduxLoading}
          emptyMessage={t('spaces.noSpacesFound')}
          tableContainerRef={scrollContainerRef}
          tableContainerSx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'scroll',
            pb: 3
          }}
          cardSx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            '& .MuiTableCell-root:last-of-type': {
              overflow: 'visible',
              whiteSpace: 'nowrap'
            }
          }}
          showLoadingMore
          loadingMoreMessage={t('dashboard.loadingMore', 'Loading more...')}
          preserveRowsOnLoading
        />
      </main>

      {/* Create Space Modal */}
      <CommonFormModal
        open={showCreateModal}
        onClose={closeCreateSpace}
        title={t("spaces.createNewSpaceTitle")}
        subtitle={t('spaces.createNewSpace')}
        onSubmit={createSpaceHandler}
        submitLabel={t("spaces.createSpaceButton")}
        cancelLabel={t("common.cancel")}
        isSubmitting={isCreatingSpace}
        isSubmitDisabled={!formData.name?.trim() || !formData.lead_id || (formData.name?.length || 0) > CHARACTER_LIMITS.SPACE_NAME}
      >
        <Box className="pm-dialog-section-title">
          {t("spaces.spaceInformation")}
          <Box
            component="img"
            src={ICONS.userIcon}
            alt=""
            aria-hidden="true"
            sx={{ width: 20, height: 20 }}
          />
        </Box>

        <Box className="pm-form-field">
          <TextField
            fullWidth
            label={t("spaces.spaceName")}
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder={t("spaces.enterSpaceName")}
            required
            variant="outlined"
            inputProps={{ maxLength: CHARACTER_LIMITS.SPACE_NAME }}
            sx={createSpaceModalStyles.field}
          />
          <Typography className="pm-character-count">
            {formData.name?.length || 0}/{CHARACTER_LIMITS.SPACE_NAME} Characters
          </Typography>
        </Box>

        <Box className="pm-form-field">
          <TextField
            fullWidth
            label={t("common.description")}
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder={t("spaces.describeSpace")}
            variant="outlined"
            multiline
            minRows={2}
            maxRows={4}
            inputProps={{ maxLength: CHARACTER_LIMITS.SPACE_DESCRIPTION }}
            sx={createSpaceModalStyles.field}
          />
          <Typography className="pm-character-count">
            {(formData.description?.length || 0).toLocaleString()}/{CHARACTER_LIMITS.SPACE_DESCRIPTION.toLocaleString()} Characters
          </Typography>
        </Box>

        <Box className="pm-form-grid">
          <FormControl fullWidth variant="outlined" sx={createSpaceModalStyles.select}>
            <InputLabel>{t("spaces.spaceLeadership")}</InputLabel>
            <Select
              name="lead_id"
              value={formData.lead_id}
              onChange={handleInputChange}
              label={t("spaces.spaceLeadership")}
              IconComponent={KeyboardArrowDown}
              MenuProps={createSpaceMenuProps}
              renderValue={(selected) => (
                <Typography component="span" sx={createSpaceModalStyles.selectValue}>
                  {getUserNameById(selected)}
                </Typography>
              )}
            >
              {users?.map(user => (
                <MenuItem key={user?.id} value={user?.id} sx={dropdownMenuItemSx}>
                  <Box sx={createSpaceModalStyles.menuUserRow}>
                    <Avatar sx={createSpaceModalStyles.menuAvatar}>
                      {getUserInitials(user?.name)}
                    </Avatar>
                    <Typography sx={createSpaceModalStyles.menuText}>
                      {user?.name || t('common.unknownUser')}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined" sx={createSpaceModalStyles.select}>
            <InputLabel>{t("spaces.activeTeamMembers", "Active Team Members")}</InputLabel>
            <Select
              multiple
              value={selectedMembers}
              onChange={handleMembersSelectChange}
              label={t("spaces.activeTeamMembers", "Active Team Members")}
              IconComponent={KeyboardArrowDown}
              MenuProps={createSpaceMenuProps}
              onOpen={() => {
                setSearchTerm('');
                setOpenedSelectedMembers([...selectedMembers]);
              }}
              renderValue={(selected) => (
                <Typography component="span" sx={createSpaceModalStyles.selectValue}>
                  {selected.length} {t("projects.members")} Selected
                </Typography>
              )}
            >
              <Box sx={{ p: 1.5, position: 'sticky', top: 0, bgcolor: COLORS.white, zIndex: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("spaces.searchUsers")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Box>
              {users?.filter(user =>
                user?.name?.toLowerCase().includes(searchTerm.toLowerCase())
              ).sort((a, b) => {
                const aSelected = openedSelectedMembers.includes(a?.id);
                const bSelected = openedSelectedMembers.includes(b?.id);
                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;
                return (a?.name || '').localeCompare(b?.name || '');
              }).map((user, index) => (
                <MenuItem key={user?.id ?? user?.email ?? `user-${index}`} value={user?.id} sx={dropdownMenuItemSx}>
                  <Checkbox checked={selectedMembers.includes(user?.id)} size="small" sx={{ p: 0.5, '&.Mui-checked': { color: '#082B72' } }} />
                  <Box sx={createSpaceModalStyles.menuUserRow}>
                    <Avatar sx={createSpaceModalStyles.menuAvatar}>
                      {getUserInitials(user?.name)}
                    </Avatar>
                    <Typography sx={createSpaceModalStyles.menuText}>
                      {user?.name || t('common.unknownUser')}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </CommonFormModal>

      {/* Edit Space Modal */}
      <CommonFormModal
        open={showEditModal}
        onClose={closeEditSpace}
        title={t("spaces.editSpaceTitle")}
        subtitle={t('spaces.updateWorkspace')}
        onSubmit={updateSpaceHandler}
        submitLabel={t("spaces.updateSpaceButton")}
        cancelLabel={t("common.cancel")}
        isSubmitting={isUpdatingSpace}
        isSubmitDisabled={isEditSpaceUnchanged || !editFormData.name?.trim() || !editFormData.lead_id || (editFormData.name?.length || 0) > CHARACTER_LIMITS.SPACE_NAME}
      >
        <Box className="pm-dialog-section-title">
          {t("spaces.spaceInformation")}
          <Box component="img" src={ICONS.userIcon} alt="" aria-hidden="true" sx={{ width: 20, height: 20 }} />
        </Box>

        <Box className="pm-form-field">
          <TextField
            fullWidth
            label={t("spaces.spaceName")}
            name="name"
            value={editFormData.name}
            onChange={handleEditInputChange}
            placeholder={t("spaces.enterSpaceName")}
            required
            variant="outlined"
            inputProps={{ maxLength: CHARACTER_LIMITS.SPACE_NAME }}
            sx={createSpaceModalStyles.field}
          />
          <Typography className="pm-character-count">
            {editFormData.name?.length || 0}/{CHARACTER_LIMITS.SPACE_NAME} Characters
          </Typography>
        </Box>

        <Box className="pm-form-field">
          <TextField
            fullWidth
            label={t("common.description")}
            name="description"
            value={editFormData.description}
            onChange={handleEditInputChange}
            placeholder={t("spaces.describeSpace")}
            variant="outlined"
            inputProps={{ maxLength: CHARACTER_LIMITS.SPACE_DESCRIPTION }}
            sx={createSpaceModalStyles.field}
          />
          <Typography className="pm-character-count">
            {(editFormData.description?.length || 0).toLocaleString()}/{CHARACTER_LIMITS.SPACE_DESCRIPTION.toLocaleString()} Characters
          </Typography>
        </Box>

        <Box className="pm-form-grid">
          <FormControl fullWidth variant="outlined" sx={createSpaceModalStyles.select}>
            <InputLabel>{t("spaces.spaceLeadership")}</InputLabel>
            <Select
              name="lead_id"
              value={editFormData.lead_id}
              onChange={handleEditInputChange}
              label={t("spaces.spaceLeadership")}
              IconComponent={KeyboardArrowDown}
              MenuProps={createSpaceMenuProps}
              renderValue={(selected) => (
                <Typography component="span" sx={createSpaceModalStyles.selectValue}>
                  {getUserNameById(selected)}
                </Typography>
              )}
            >
              {users?.map(user => (
                <MenuItem key={user?.id} value={user?.id} sx={dropdownMenuItemSx}>
                  <Box sx={createSpaceModalStyles.menuUserRow}>
                    <Avatar sx={createSpaceModalStyles.menuAvatar}>
                      {getUserInitials(user?.name)}
                    </Avatar>
                    <Typography sx={createSpaceModalStyles.menuText}>
                      {user?.name || t('common.unknownUser')}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined" sx={createSpaceModalStyles.select}>
            <InputLabel>{t("spaces.activeTeamMembers", "Active Team Members")}</InputLabel>
            <Select
              multiple
              value={editSelectedMembers}
              onChange={(e) => {
                const val = e.target.value;
                const next = typeof val === 'string' ? val.split(',') : val;
                if (editFormData.lead_id && !next.includes(editFormData.lead_id)) {
                  setEditFormData(prev => ({ ...prev, lead_id: '' }));
                }
                setEditSelectedMembers(next);
              }}
              label={t("spaces.activeTeamMembers", "Active Team Members")}
              IconComponent={KeyboardArrowDown}
              MenuProps={createSpaceMenuProps}
              onOpen={() => {
                setSearchTerm('');
                setOpenedEditSelectedMembers([...editSelectedMembers]);
              }}
              renderValue={(selected) => (
                <Typography component="span" sx={createSpaceModalStyles.selectValue}>
                  {selected.length} {t("projects.members")} Selected
                </Typography>
              )}
            >
              <Box sx={{ p: 1.5, position: 'sticky', top: 0, bgcolor: COLORS.white, zIndex: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("spaces.searchUsers")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
                />
              </Box>
              {users?.filter(user =>
                user?.name?.toLowerCase().includes(searchTerm.toLowerCase())
              ).sort((a, b) => {
                const aSelected = openedEditSelectedMembers.includes(a?.id);
                const bSelected = openedEditSelectedMembers.includes(b?.id);
                if (aSelected && !bSelected) return -1;
                if (!aSelected && bSelected) return 1;
                return (a?.name || '').localeCompare(b?.name || '');
              }).map((user, index) => (
                <MenuItem key={user?.id ?? user?.email ?? `user-${index}`} value={user?.id} sx={dropdownMenuItemSx}>
                  <Checkbox checked={editSelectedMembers.includes(user?.id)} size="small" sx={{ p: 0.5, '&.Mui-checked': { color: '#082B72' } }} />
                  <Box sx={createSpaceModalStyles.menuUserRow}>
                    <Avatar sx={createSpaceModalStyles.menuAvatar}>
                      {getUserInitials(user?.name)}
                    </Avatar>
                    <Typography sx={createSpaceModalStyles.menuText}>
                      {user?.name || t('common.unknownUser')}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </CommonFormModal>

      <CommonDeleteConfirmDialog
        open={showDeleteModal}
        onClose={closeDeleteSpace}
        onConfirm={confirmDeleteSpace}
        title={t("spaces.deleteSpaceButton")}
        question={t("spaces.deleteConfirmation")}
        itemName={selectedSpace?.name}
        warning={t("spaces.deleteWarning")}
        confirmText={t("spaces.deleteSpaceButton")}
        cancelText={t("common.cancel")}
      />

      <TeamMembersDialog
        open={showMembersModal}
        onClose={closeMembersModal}
        title={selectedSpace?.name}
        subtitle={`${t('common.space')} ${t('spaces.teamMembers')}`}
        members={getSpaceMembers(selectedSpace)}
        leadName={selectedSpace?.owner_name}
        labels={{
          totalMembers: t('projects.totalMembers'),
          teamMembers: t('spaces.teamMembers'),
          unknownUser: t('common.unknownUser'),
          lead: t('projects.lead'),
          active: t('common.active'),
          close: t('projects.close')
        }}
      />
    </div>
  );
};

export default SpaceManagement;
